/**
 * Der Weg ueber Funk: Web Bluetooth.
 *
 * Der Roboter bietet einen Nordic-UART-Dienst an und haengt ihn an dieselbe
 * Konsole, die auch am Kabel haengt (python/robot/_ble.py). Von hier aus ist
 * BLE deshalb nichts weiter als eine zweite Leitung fuer denselben Text - das
 * Uploadprotokoll, die Telemetrie und der Rueckkanal merken den Unterschied
 * nicht.
 *
 * Was am Geraet gemessen wurde (docs/messungen/2026-09-11-ble.md):
 * die ausgehandelte Nachrichtengroesse war 527 Byte, uebertragen wurden im
 * Dauerlauf 116 kB/s, und Telemetrie bei 50 Hz kostet den Regelkern
 * hoechstens 1,1 Prozentpunkte Auslastung.
 */

import { DeviceError } from "@microbit/microbit-connection";
import { fehlerDaten, protokoll } from "./protokoll";
import type { Transport, TransportHooks } from "./transport";

/** Nordic UART. Muss zu python/robot/_ble.py passen. */
export const UART_SERVICE = "6e400001-b5a3-f393-e0a9-e50e24dcca9e";
export const UART_RX = "6e400002-b5a3-f393-e0a9-e50e24dcca9e";
export const UART_TX = "6e400003-b5a3-f393-e0a9-e50e24dcca9e";

/**
 * Groesse eines Schreibvorgangs zum Roboter.
 *
 * 20 Byte sind die Nutzlast der kleinsten Nachricht, die jede Gegenstelle
 * koennen muss (23 Byte minus drei Byte Kopf). Grosszuegiger geht es nicht,
 * und zwar aus einem Grund, der teuer war:
 *
 * ``writeValueWithoutResponse`` muss in **eine** Nachricht passen. Web
 * Bluetooth verraet die ausgehandelte Groesse aber nicht. Eine fruehere
 * Fassung schrieb deshalb versuchsweise 244 Byte und wollte kleiner werden,
 * sobald ein Schreibvorgang scheitert. Das kann nicht funktionieren: Was nicht
 * passt, **wird abgeschnitten und nicht abgewiesen** - auf der Bluetooth-
 * Schicht von macOS ebenso wie im Roboter selbst
 * (``modbluetooth_btstack.c:947``). Es gibt keinen Fehler, auf den man
 * reagieren koennte; es fehlen einfach Bytes. Fuer die REPL ist ein
 * Datenstrom mit Luecken wertlos, und im Editor sieht das aus wie eine
 * fehlende Firmware.
 *
 * Ein groesserer Block ist erst dann vertretbar, wenn der Roboter die
 * ausgehandelte Groesse selbst mitteilt und sie nachgemessen ist. Bis dahin
 * gilt der Wert, der immer stimmt.
 */
const CHUNK = 20;

/**
 * So lange wird nach einem Verbindungsabriss neu verbunden, bevor aufgegeben
 * wird.
 *
 * Ein Abriss gehoert hier zum **normalen** Ablauf und ist kein Fehler: nach
 * dem Upload startet die Oberflaeche das Programm mit einem weichen Reset,
 * und der schaltet den Funkbaustein ab (``mp_bluetooth_deinit`` in
 * ``ports/rp2/main.c:280``). Ohne Wiederaufbau waere der Roboter nach jedem
 * Upload weg - ausgerechnet in dem Moment, in dem das neue Programm anlaeuft.
 *
 * Zehn Sekunden, weil das Board danach neu startet, das Programm anlaeuft und
 * ``ble.start`` wieder wirbt. Am Geraet gemessen brauchte der Aufbau des
 * Funkbausteins 59 ms; der Rest ist das Programm selbst.
 */
const RECONNECT_WINDOW_MS = 10000;

/** Pause zwischen zwei Verbindungsversuchen. */
const RECONNECT_WAIT_MS = 400;

export class BluetoothTransport implements Transport {
  readonly kind = "bluetooth" as const;

  private device: BluetoothDevice | undefined;
  private rx: BluetoothRemoteGATTCharacteristic | undefined;
  private tx: BluetoothRemoteGATTCharacteristic | undefined;
  private hooks: TransportHooks | undefined;
  /** Solange wahr, ist ein Abriss ungewollt und wird geheilt. */
  private wanted = false;
  private reconnecting = false;

  isSupported(): boolean {
    return typeof navigator !== "undefined" && Boolean(navigator.bluetooth);
  }

  async hasAuthorizedDevice(): Promise<boolean> {
    // getDevices gibt es nicht ueberall, und wo es fehlt, ist die Antwort
    // schlicht "wir muessen fragen".
    const bluetooth: (Bluetooth & {
      getDevices?: () => Promise<BluetoothDevice[]>;
    }) | undefined = navigator.bluetooth;
    if (!bluetooth?.getDevices) {
      return false;
    }
    try {
      return (await bluetooth.getDevices()).length > 0;
    } catch {
      return false;
    }
  }

  async open(hooks: TransportHooks): Promise<void> {
    if (!this.isSupported()) {
      throw new DeviceError({
        code: "unsupported",
        message:
          "Dieser Browser kann kein Bluetooth ansprechen. " +
          "Der Lehrroboter braucht Chrome oder Edge.",
      });
    }
    this.hooks = hooks;

    protokoll.ereignis("funk", "geraeteauswahl");
    hooks.beforeRequestDevice();
    let device: BluetoothDevice;
    try {
      // Nach dem **Dienst** filtern und nicht nach dem Namen: im Kursraum
      // werben zwanzig Roboter, und der Dienst blendet alles andere aus.
      device = await navigator.bluetooth!.requestDevice({
        filters: [{ services: [UART_SERVICE] }],
      });
      protokoll.ereignis("funk", "geraet gewaehlt", {
        name: device.name,
        id: device.id,
      });
    } catch (error) {
      protokoll.ereignis("funk", "keine auswahl", fehlerDaten(error));
      throw new DeviceError({
        code: "no-device-selected",
        message:
          "Es wurde kein Roboter ausgewaehlt. Er muss dafuer werben: " +
          "ble.start(\"Name\") im Programm auf dem Roboter.",
        cause: error,
      });
    } finally {
      hooks.afterRequestDevice();
    }

    this.device = device;
    device.addEventListener("gattserverdisconnected", this.handleDisconnect);
    try {
      await this.attach(device);
    } catch (error) {
      protokoll.ereignis("funk", "verbinden fehlgeschlagen", fehlerDaten(error));
      throw error;
    }
    this.wanted = true;
    protokoll.ereignis("funk", "offen");
  }

  /** Verbindet und abonniert die Meldungen. Auch der Wiederaufbau geht hier durch. */
  private async attach(device: BluetoothDevice): Promise<void> {
    const beginn = performance.now();
    const schritt = (was: string) =>
      protokoll.ereignis("funk", was, {
        seit_beginn_ms: Math.round(performance.now() - beginn),
        gatt_verbunden: device.gatt?.connected,
      });
    schritt("gatt verbinden");
    const server = await device.gatt!.connect();
    schritt("gatt verbunden");
    const service = await server.getPrimaryService(UART_SERVICE);
    schritt("dienst gefunden");
    this.rx = await service.getCharacteristic(UART_RX);
    this.tx = await service.getCharacteristic(UART_TX);
    schritt("kennungen gefunden");
    this.tx.addEventListener(
      "characteristicvaluechanged",
      this.handleNotification
    );
    await this.tx.startNotifications();
    schritt("meldungen abonniert");
  }

  private handleNotification = (event: Event): void => {
    const value = (event.target as BluetoothRemoteGATTCharacteristic).value;
    if (value && this.hooks) {
      const daten = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
      protokoll.datenverkehr("funk", "rein", daten);
      this.hooks.onData(daten);
    }
  };

  private handleDisconnect = (): void => {
    protokoll.ereignis("funk", "getrennt", {
      gewollt: !this.wanted,
      beim_wiederverbinden: this.reconnecting,
    });
    if (!this.wanted) {
      // Selbst getrennt. Kein Grund, etwas zu heilen oder zu melden.
      return;
    }
    void this.reconnect();
  };

  /**
   * Baut die Verbindung nach einem Abriss wieder auf.
   *
   * Erst wenn das Fenster verstrichen ist, gilt der Roboter als weg. Ein
   * weicher Reset nach dem Upload sieht von hier aus genauso aus wie ein
   * Roboter, der aus der Reichweite faehrt - der Unterschied ist nur, dass
   * der eine wiederkommt.
   */
  private async reconnect(): Promise<void> {
    if (this.reconnecting) {
      return;
    }
    this.reconnecting = true;
    this.rx = undefined;
    this.tx = undefined;
    const beginn = Date.now();
    const bis = beginn + RECONNECT_WINDOW_MS;
    let versuch = 0;
    protokoll.ereignis("funk", "wiederverbinden beginnt");
    try {
      while (this.wanted && Date.now() < bis) {
        await new Promise((weiter) => setTimeout(weiter, RECONNECT_WAIT_MS));
        if (!this.wanted || !this.device) {
          protokoll.ereignis("funk", "wiederverbinden abgebrochen", {
            gewollt: this.wanted,
          });
          return;
        }
        versuch += 1;
        try {
          await this.attach(this.device);
          protokoll.ereignis("funk", "wieder verbunden", {
            versuche: versuch,
            dauer_ms: Date.now() - beginn,
          });
          this.hooks?.onReconnected?.();
          return;
        } catch (error) {
          // Der Roboter wirbt noch nicht wieder. Weiter versuchen.
          protokoll.ereignis("funk", "wiederverbinden versuch", {
            versuch,
            ...fehlerDaten(error),
          });
        }
      }
      if (this.wanted) {
        protokoll.ereignis("funk", "verbindung verloren", {
          versuche: versuch,
          dauer_ms: Date.now() - beginn,
        });
        this.hooks?.onLost();
      }
    } finally {
      this.reconnecting = false;
    }
  }

  /**
   * Schreibauftraege laufen nacheinander, nie ueberlappend.
   *
   * Ohne diese Kette durfte jeder Aufrufer jederzeit in ``write`` hinein: die
   * Telemetrieanzeige, der Rueckkanal, das Uploadprotokoll, die Konsole. Beim
   * Ziehen am Joystick sind das bis zu 120 Aufrufe je Sekunde, und sie
   * ueberlappten sich - mit zwei Folgen. Erstens weist Web Bluetooth einen
   * Schreibvorgang ab, waehrend ein anderer laeuft. Zweitens schieben sich
   * sonst 20-Byte-Bloecke fremder Aufrufer mitten in einen Programmupload.
   *
   * Beide Sendestellen des Rueckkanals riefen ausserdem ohne ``await`` und
   * ohne ``catch`` auf. Ein abgewiesener Schreibvorgang verschwand damit
   * spurlos - er tauchte nicht einmal in der Konsole auf. Genau so ging die
   * ``[0, 0]`` verloren, mit der ein losgelassener Joystick den Roboter
   * anhaelt: sie wird unmittelbar nach der letzten Bewegung geschickt, also im
   * denkbar unguenstigsten Augenblick.
   *
   * Die Serielle hatte das Problem nie: ein ``WritableStreamDefaultWriter``
   * reiht Auftraege von sich aus ein. Hier wird dasselbe von Hand gemacht.
   */
  private kette: Promise<unknown> = Promise.resolve();

  async write(text: string): Promise<void> {
    // Auch ein gescheiterter Vorgaenger darf den Nachfolger nicht aufhalten:
    // die Kette wartet auf sein Ende, nicht auf sein Gelingen.
    const auftrag = this.kette.catch(() => undefined).then(() => this.schreibeJetzt(text));
    this.kette = auftrag;
    return auftrag;
  }

  private async schreibeJetzt(text: string): Promise<void> {
    const rx = this.rx;
    if (!rx) {
      protokoll.ereignis("funk", "schreiben ohne verbindung", {
        bytes: text.length,
        beim_wiederverbinden: this.reconnecting,
        vorschau: text.slice(0, 40),
      });
      throw new DeviceError({
        code: "not-connected",
        message: "Es besteht keine Funkverbindung zum Roboter.",
      });
    }
    const bytes = new TextEncoder().encode(text);
    const beginn = performance.now();
    let start = 0;
    try {
      for (; start < bytes.length; start += CHUNK) {
        // Ohne Bestaetigung: mit waere jeder Block ein eigener Umlauf, und ein
        // Programm von 3 kB brauchte ein Vielfaches der Zeit.
        await rx.writeValueWithoutResponse(bytes.slice(start, start + CHUNK));
      }
    } catch (error) {
      protokoll.ereignis("funk", "schreiben fehlgeschlagen", {
        bytes: bytes.length,
        geschrieben: start,
        ...fehlerDaten(error),
      });
      throw error;
    }
    protokoll.datenverkehr("funk", "raus", bytes, {
      dauer_ms: Math.round(performance.now() - beginn),
    });
  }

  async close(): Promise<void> {
    protokoll.ereignis("funk", "schliessen");
    this.wanted = false;
    if (this.tx) {
      this.tx.removeEventListener(
        "characteristicvaluechanged",
        this.handleNotification
      );
      await this.tx.stopNotifications().catch(() => undefined);
      this.tx = undefined;
    }
    this.rx = undefined;
    if (this.device) {
      // Erst den Zuhoerer abmelden, dann trennen: sonst meldet das eigene
      // Trennen einen Verbindungsverlust, und die Oberflaeche zeigt einen
      // Fehler fuer etwas, das der Nutzer selbst ausgeloest hat.
      this.device.removeEventListener(
        "gattserverdisconnected",
        this.handleDisconnect
      );
      this.device.gatt?.disconnect();
    }
  }

  async forget(): Promise<void> {
    await this.close();
    const device: (BluetoothDevice & {
      forget?: () => Promise<void>;
    }) | undefined = this.device;
    if (device?.forget) {
      await device.forget().catch(() => undefined);
    }
    this.device = undefined;
  }

  dispose(): void {
    void this.close();
  }
}
